import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ThumbsUp, ThumbsDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Inline answer body renderer.
 *
 * The FAQ seed uses light markdown-style links "[label](/path)" so operators
 * can weave in on-site interlinks. We parse those into <Link> nodes at
 * render time and leave the rest as plain text. No HTML injection risk —
 * everything not matching the pattern is passed through React as text.
 */
export function FaqAnswer({ text }) {
  const parts = useMemo(() => {
    const nodes = [];
    const re = /\[([^\]]+)\]\((\/[^\s)]+)\)/g;
    let lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }
      const [, label, href] = match;
      nodes.push(
        <Link key={`${match.index}-${href}`} to={href} className="text-primary underline underline-offset-2 hover:no-underline">
          {label}
        </Link>
      );
      lastIndex = re.lastIndex;
    }
    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }
    return nodes;
  }, [text]);
  return <p className="text-sm text-muted-foreground leading-relaxed">{parts}</p>;
}

/**
 * "Was this helpful?" widget — writes to /api/faq/{slug}/helpful and remembers
 * the vote per slug in localStorage so users don't repeat-vote the same item.
 */
function HelpfulWidget({ slug }) {
  const storageKey = `faq_helpful_${slug}`;
  const [vote, setVote] = useState(() => {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const submit = async (helpful) => {
    if (vote) return;
    setVote(helpful ? "yes" : "no");
    try {
      localStorage.setItem(storageKey, helpful ? "yes" : "no");
    } catch {}
    try {
      await api.post(`/faq/${slug}/helpful`, { helpful });
    } catch {
      // Silent — vote already reflected in UI, and this is a soft signal.
    }
  };
  if (vote === "yes") {
    return <span className="text-[11px] text-emerald-600 font-mono">Thanks for the feedback.</span>;
  }
  if (vote === "no") {
    return <span className="text-[11px] text-amber-600 font-mono">We'll improve this answer.</span>;
  }
  return (
    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
      <span className="uppercase tracking-wider font-mono">Was this helpful?</span>
      <button
        type="button"
        onClick={() => submit(true)}
        data-testid={`faq-helpful-yes-${slug}`}
        className="inline-flex items-center gap-1 hover:text-emerald-600"
      >
        <ThumbsUp className="h-3.5 w-3.5" /> Yes
      </button>
      <button
        type="button"
        onClick={() => submit(false)}
        data-testid={`faq-helpful-no-${slug}`}
        className="inline-flex items-center gap-1 hover:text-amber-600"
      >
        <ThumbsDown className="h-3.5 w-3.5" /> No
      </button>
    </div>
  );
}

/**
 * Copy-anchor button + heading. Clicking the icon copies the /faq#<slug> URL
 * to the clipboard for citation.
 */
function AnchorHeading({ slug, question, level = "h3" }) {
  const Tag = level;
  const copy = async () => {
    const url = `${window.location.origin}/faq#${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {}
  };
  return (
    <div id={slug} className="flex items-start gap-2 group">
      <Tag className="font-display font-semibold text-base sm:text-lg text-foreground flex-1 scroll-mt-24" data-testid={`faq-q-${slug}`}>
        <a href={`/faq#${slug}`} onClick={(e) => { e.preventDefault(); copy(); }} className="hover:text-primary">
          {question}
        </a>
      </Tag>
    </div>
  );
}

/**
 * FaqBlock — a lightweight, embeddable FAQ block for contextual placement on
 * product/dashboard/wizard pages. Fetches only the requested slugs and emits
 * Question schema JSON-LD for each so Google + AI answer engines can extract
 * them directly. Deduplicates against the main /faq page emission by using
 * the individual `Question` schema (not `FAQPage`).
 *
 * Props:
 *   title  — optional H2 heading rendered above the block
 *   slugs  — ordered array of FAQ slugs to render
 *   href   — optional "See all" link (defaults to /faq)
 */
export default function FaqBlock({ title = "Frequently asked", slugs = [], href = "/faq", testid = "faq-block" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slugs.length) { setLoading(false); return; }
    const csv = slugs.join(",");
    api.get(`/faq/by-slugs?slugs=${encodeURIComponent(csv)}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slugs.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Question schema — one JSON-LD block per item.
  const schemas = items.map((it) => ({
    "@context": "https://schema.org",
    "@type": "Question",
    "name": it.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": it.answer.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1"),
    },
  }));

  if (loading || items.length === 0) return null;

  return (
    <section
      data-testid={testid}
      className="mt-10 border border-border rounded-lg bg-card p-5 sm:p-6"
    >
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }} />
      ))}
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-display font-semibold text-lg">{title}</h2>
        <Link
          to={href}
          className="text-xs font-mono uppercase tracking-wider text-primary hover:underline inline-flex items-center gap-1"
          data-testid={`${testid}-see-all`}
        >
          See all FAQs <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="divide-y divide-border">
        {items.map((it) => (
          <details key={it.slug} className="py-3 group" data-testid={`faq-item-${it.slug}`}>
            <summary className="cursor-pointer list-none flex items-start gap-3">
              <ChevronRight className="h-4 w-4 mt-1 text-primary transition-transform group-open:rotate-90" />
              <AnchorHeading slug={it.slug} question={it.question} level="h3" />
            </summary>
            <div className="mt-3 pl-7 space-y-3">
              <FaqAnswer text={it.answer} />
              <HelpfulWidget slug={it.slug} />
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

// Export a couple of pre-built slug lists so pages don't hard-code them
// (keeps the "which FAQs go where" decision in one file).
export const FAQ_SLUGS = {
  payments: ["how-do-designers-get-paid", "how-long-does-payment-take", "why-48-hour-hold", "what-is-deducted-from-earnings", "do-i-need-kyc-to-receive-payments"],
  fileFormats: ["difference-between-3dm-and-stl", "which-format-for-3d-printing", "can-i-edit-an-stl-file", "why-every-3dm-includes-stl"],
  camVendors: ["what-is-cam-in-jewellery", "how-do-i-find-a-3d-printing-vendor", "which-3d-printers-for-jewellery-casting", "how-is-print-job-price-decided", "what-file-does-cam-vendor-receive"],
  howItWorks: ["how-do-designers-get-paid", "why-48-hour-hold", "what-happens-if-a-file-doesnt-work", "can-i-see-a-design-before-buying"],
};

export { HelpfulWidget, AnchorHeading };
